import { useMutation } from "@apollo/client";
import moment from "moment";
import { useMemo } from "react";
import {
  ActionItemArtifactNode,
  BasicGoalArtifactNode,
  GoalArtifactNode,
  GoalState,
  PermissionNode,
} from "types/graphql-schema";

import getArtifactActivitiesQuery from "@apps/artifact-sidebar/graphql/get-artifact-activities-query";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import InputDatePicker from "@components/input/input-datepicker";

import updateArtifactMutation from "../graphql/update-artifact-mutation";

const ArtifactDueDate = ({
  artifact,
}: {
  artifact:
    | (Pick<
        BasicGoalArtifactNode,
        "id" | "dueDate" | "state" | "__typename"
      > & {
        canUpdate: Pick<PermissionNode, "permission">;
      })
    | (Pick<GoalArtifactNode, "id" | "dueDate" | "state" | "__typename"> & {
        canUpdate: Pick<PermissionNode, "permission">;
      })
    | (Pick<
        ActionItemArtifactNode,
        "id" | "dueDate" | "isComplete" | "__typename"
      > & {
        canUpdate: Pick<PermissionNode, "permission">;
      });
}) => {
  const [updateArtifact] = useMutation(updateArtifactMutation);

  const handleSetDueDate = (date: Date | null) => {
    const dueDate = date ? moment(date).format("YYYY-MM-DD") : null;
    updateArtifact({
      variables: {
        artifactId: artifact.id,
        additionalFields: { dueDate },
      },
      optimisticResponse: {
        createOrUpdateArtifact: {
          artifact: {
            ...artifact,
            dueDate,
          },
          __typename: "CreateOrUpdateArtifactMutation",
        },
      },
      refetchQueries: [getArtifactActivitiesQuery],
    });
  };

  const isCompleted = useMemo(
    () =>
      artifact.__typename === "ActionItemArtifactNode"
        ? artifact.isComplete
        : artifact.state === GoalState.Closed,
    [artifact],
  );

  const isOverdue = useMemo(
    () =>
      !isCompleted &&
      artifact.dueDate &&
      moment(artifact.dueDate).isBefore(moment()),
    [artifact.dueDate, isCompleted],
  );

  return (
    <InputDatePicker
      date={artifact.dueDate}
      onChangeDate={handleSetDueDate}
      isClearable={true}
      placeholderText=""
      buttonProps={{
        theme: isOverdue ? buttonTheme.textRed : buttonTheme.text,
        size: ButtonSize.small,
        tooltip: artifact.dueDate
          ? `Due on ${moment(artifact.dueDate).format("dddd, MMMM D, YYYY")}`
          : "Add due date",
      }}
      showIcon={!artifact.dueDate}
    />
  );
};

export default ArtifactDueDate;
